import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarCheck, Target, RotateCcw, ArrowRightCircle, Sparkles, BookOpen, Flag, Rocket,
  Clock, RefreshCw, ArrowLeft, Star, Flame, TrendingUp, AlertCircle,
} from "lucide-react";
import Layout from "../../components/Layout/Layout";
import api from "../../services/api";
import "./RecommendationAgent.css";

// How each kind of step looks
const KINDS = {
  today: { label: "Planned for today", icon: CalendarCheck, tone: "blue" },
  weak: { label: "Needs practice", icon: Target, tone: "red" },
  missed: { label: "Review", icon: RotateCcw, tone: "amber" },
  continue: { label: "Keep going", icon: ArrowRightCircle, tone: "amber" },
  next: { label: "Next chapter", icon: BookOpen, tone: "green" },
  stretch: { label: "Challenge", icon: Flag, tone: "purple" },
  new: { label: "Something new", icon: Rocket, tone: "navy" },
  start: { label: "Get started", icon: Sparkles, tone: "navy" },
};

const TREND = { improving: "Improving", steady: "Steady", slipping: "Slipping" };

const RecommendationAgent = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // Who you are comes from your sign-in, so nothing needs to be sent
      const res = await api.post("/api/agents/recommendations", {}, { timeout: 90000 });
      setData(res.data);
    } catch (e) {
      setData(null);
      setError(
        e?.response?.data?.detail && typeof e.response.data.detail === "string"
          ? e.response.data.detail
          : "We couldn't work out your next steps just now. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const stats = data?.stats || {};
  const hasStats = stats.average != null;

  return (
    <div className="rec-page">
      <button type="button" className="rec-back" onClick={() => navigate("/student-dashboard")}>
        <ArrowLeft size={16} aria-hidden="true" /> Back to dashboard
      </button>

      <header className="rec-head">
        <div>
          <h1>Your next steps</h1>
          <p>{data?.headline || "Working out what will help you most…"}</p>
        </div>
        <button type="button" className="rec-refresh" onClick={load} disabled={loading} aria-label="Refresh">
          <RefreshCw size={16} className={loading ? "spin" : ""} aria-hidden="true" /> Refresh
        </button>
      </header>

      {error && (
        <div className="rec-alert" role="alert">
          <AlertCircle size={18} aria-hidden="true" />
          <span>{error}</span>
          <button type="button" onClick={load}>Try again</button>
        </div>
      )}

      {loading && !data && (
        <div className="rec-skeleton" aria-busy="true" aria-label="Loading your next steps">
          {[0, 1, 2].map((i) => <div key={i} className="rec-skel-card" />)}
        </div>
      )}

      {data && (
        <>
          {hasStats && (
            <div className="rec-stats" aria-label="Where you are now">
              <div><span>Level</span><strong>{stats.level || "—"}</strong></div>
              <div><span>Quiz average</span><strong>{stats.average}%</strong></div>
              <div>
                <span>Trend</span>
                <strong>{stats.trend ? <><TrendingUp size={14} aria-hidden="true" /> {TREND[stats.trend]}</> : "—"}</strong>
              </div>
              <div><span>Quizzes</span><strong>{stats.quizzes}</strong></div>
              <div><span>Streak</span><strong><Flame size={14} aria-hidden="true" /> {stats.streak || 0} day{stats.streak === 1 ? "" : "s"}</strong></div>
            </div>
          )}

          {data.coach_note && (
            <aside className="rec-coach">
              <Sparkles size={18} aria-hidden="true" />
              <p>{data.coach_note}</p>
            </aside>
          )}

          {data.steps?.length > 0 ? (
            <ol className="rec-steps">
              {data.steps.map((step) => {
                const kind = KINDS[step.kind] || KINDS.start;
                const Icon = kind.icon;
                return (
                  <li key={step.id} className={`rec-step tone-${kind.tone}`}>
                    <span className="rec-rank" aria-hidden="true">{step.rank}</span>
                    <div className="rec-body">
                      <div className="rec-top">
                        <span className="rec-kind"><Icon size={14} aria-hidden="true" /> {kind.label}</span>
                        {step.subject && <span className="rec-subject">{step.subject}</span>}
                        <span className="rec-time"><Clock size={13} aria-hidden="true" /> about {step.minutes} min</span>
                      </div>
                      <h2>{step.title}</h2>
                      <p className="rec-why">{step.why}</p>
                      <div className="rec-actions">
                        {step.actions.map((a) => (
                          <button
                            key={a.label}
                            type="button"
                            className={`eg-btn ${a.primary ? "eg-btn-accent" : "eg-btn-outline"}`}
                            onClick={() => navigate(a.path)}
                          >
                            {a.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          ) : (
            <div className="rec-empty">
              <Star size={28} aria-hidden="true" />
              <p>You're all caught up. Take a new quiz or start a course to keep your streak going.</p>
              <div className="rec-actions center">
                <button type="button" className="eg-btn eg-btn-accent" onClick={() => navigate("/agents/quiz")}>Take a quiz</button>
                <button type="button" className="eg-btn eg-btn-outline" onClick={() => navigate("/courses")}>Browse courses</button>
              </div>
            </div>
          )}

          {data.strengths?.length > 0 && (
            <section className="rec-strengths">
              <h3><Star size={16} aria-hidden="true" /> What's going well</h3>
              <div>
                {data.strengths.map((s) => (
                  <span key={`${s.subject}-${s.topic}`} className="rec-chip">{s.topic} · {s.subject} ({s.score}%)</span>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
};

// Wrap in the shared layout so the page has the site header and footer
const RecommendationAgentPage = (props) => (
  <Layout>
    <RecommendationAgent {...props} />
  </Layout>
);

export default RecommendationAgentPage;
